import { Injectable, inject } from '@angular/core'
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpSentEvent,
    HttpHeaderResponse,
    HttpProgressEvent,
    HttpResponse,
    HttpUserEvent,
    HttpErrorResponse,
} from '@angular/common/http'
import { Observable, defer, throwError as _throw } from 'rxjs'
import { FirebaseError } from 'firebase/app'
import { catchError, take, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { FirebaseTokenService } from '@app/core/_services/firebase-token.service'

@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
    private tokenRefresh$: Observable<string> | null = null

    constructor(private firebaseTokenService: FirebaseTokenService) {}

    addToken(req: HttpRequest<any>): HttpRequest<any> {
        return req.clone({
            setHeaders: {
                Authorization: this.firebaseTokenService.getToken(),
            },
        })
    }

    addAnonymous(req: HttpRequest<any>): HttpRequest<any> {
        return req.clone({
            setHeaders: {
                Authorization: '',
            },
        })
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<
        | HttpSentEvent
        | HttpHeaderResponse
        | HttpProgressEvent
        | HttpResponse<any>
        | HttpUserEvent<any>
    > {
        if (req.headers.get('Anonymous') === '') {
            return next.handle(this.addAnonymous(req)).pipe(
                catchError((error) => {
                    if (error instanceof HttpErrorResponse) {
                        switch ((<HttpErrorResponse>error).status) {
                            case 401:
                                if (
                                    this.firebaseTokenService.checkTokenExpiredError(error) === true
                                ) {
                                    return this.handle401Error(req, next)
                                } else {
                                    return _throw(error)
                                }

                            default:
                                return _throw(error)
                        }
                    } else {
                        return _throw(error)
                    }
                }),
            )
        } else {
            return next.handle(this.addToken(req)).pipe(
                catchError((error) => {
                    if (error instanceof HttpErrorResponse) {
                        switch ((<HttpErrorResponse>error).status) {
                            case 401:
                                if (
                                    this.firebaseTokenService.checkTokenExpiredError(error) === true
                                ) {
                                    return this.handle401Error(req, next)
                                } else {
                                    return _throw(error)
                                }

                            default:
                                return _throw(error)
                        }
                    } else {
                        return _throw(error)
                    }
                }),
            )
        }
    }

    handle401Error(req: HttpRequest<any>, next: HttpHandler) {
        if (!this.tokenRefresh$) {
            const refresh$ = defer(() => this.firebaseTokenService.refreshToken()).pipe(
                take(1),
                map((token: string) => {
                    if (!token) {
                        throw new FirebaseError('auth/no-current-user', 'Please sign in again.')
                    }
                    return token
                }),
                tap((token) => this.firebaseTokenService.setToken(token)),
                catchError((error) => this.firebaseTokenService.handleRefreshFailure(error)),
                finalize(() => {
                    if (this.tokenRefresh$ === refresh$) {
                        this.tokenRefresh$ = null
                    }
                }),
                // The finite refresh owns error delivery for every waiting request.
                shareReplay({ bufferSize: 1, refCount: false }),
            )
            this.tokenRefresh$ = refresh$
        }

        return this.tokenRefresh$.pipe(
            // Retry once. Errors from this request must not invalidate a successful refresh.
            switchMap(() => next.handle(this.addToken(req))),
        )
    }
}
